import { fromJS, Map } from 'immutable';
import * as Sentry from '@sentry/react';

import { getDiscountedPrice, isSubscribed } from '../utils/billing';
import { isEbayInvalidMarketplace } from '../utils/shops';
import { createController } from '../utils/controller';
import { navigateTo } from '../utils/navigation';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { BETA_CHANNELS, FEATURE } from '../constants/features';
import { EBAY, ETSY, FAIRE } from '../constants/channels';
import { PLAN, TIER } from '../constants/billing';
import { STATUSES } from '../constants/shops';
import { ENDPOINT } from '../constants/api';
import { MODALS } from '../constants/modal';
import { EMPTY } from '../constants';

import actions from '../actions';

const customerController = createController();
const invoicesController = createController();
const shopsController = createController();
const manageController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    customerController,
    invoicesController,
    shopsController,
    manageController,
  ].forEach(abort);
}

function* cleanUp(reduction) {
  abortAll();
  return reduction.set('billing', EMPTY.MAP);
}

function* getCustomer(reduction) {
  abort(customerController);

  const { signal } = customerController.start();

  yield function effect(dispatch) {
    api.billing
      .getCustomer({ signal })
      .then(
        (response) => dispatch(actions.billing.getCustomer.fulfilled(response)),
        (error) => dispatch(actions.billing.getCustomer.rejected({ error, signal })),
      );
  };

  return reduction;
}

function* getCustomerFulfilled(reduction, response) {
  abort(customerController);

  let billing = reduction.get('billing');

  if (billing.has('paymentError')) {
    billing = billing.delete('paymentError');
  }

  billing = billing.set('customer', fromJS(response));

  return reduction.set('billing', billing);
}

function* getCustomerRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(customerController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* getInvoices(reduction) {
  function reduceShopIds(result, shopId) {
    const subscriptionId = reduction.getIn(['user', 'subscriptions', shopId, 'subscriptionId']);

    if (!subscriptionId) return result;

    const shop = reduction.getIn(['shops', 'byId', shopId]);

    return result.push(
      Map({
        channel: shop.get('channel'),
        domain: shop.get('domain'),
        db: shop.get('db'),
        shopId,
        shopName: shop.get('name'),
        subscriptionId,
      })
    );
  }

  const tabs = reduction.getIn(['shops', 'options']).reduce(reduceShopIds, EMPTY.LIST);

  if (getSize(tabs)) {
    yield function effect(dispatch) {
      dispatch(actions.billing.setInvoicesTab(0));
    };
  }

  return reduction.setIn(['billing', 'invoices'], Map({ tabs }));
}

function* getShops(reduction) {
  abort(shopsController);

  const empty = Map({ amount: 0, byId: EMPTY.MAP, connected: 0, ids: EMPTY.LIST, upgraded: 0 });
  const subscriptions = reduction.getIn(['user', 'subscriptions']);

  if (!getSize(subscriptions)) return reduction.setIn(['billing', 'shops'], empty);

  const userShops = reduction.get('shops');
  const { signal } = shopsController.start();
  const isBillingV3Enabled = reduction.getIn(['user', 'features', FEATURE.BILLING_V3]);

  function reduceShopIds(result, shopId) {
    const subscription = reduction.getIn(['user', 'subscriptions', shopId]);

    if (!subscription) return result;

    const shop = userShops.getIn(['byId', shopId]);
    const channel = shop.get('channel');
    const plan = subscription.get('plan');
    const db = shop.get('db');
    const shopName = shop.get('name');

    if (plan === PLAN.FREE || (BETA_CHANNELS.has(channel) || (channel === FAIRE && !isBillingV3Enabled))) {
      const shopBadge = (BETA_CHANNELS.has(channel) || (channel === FAIRE && !isBillingV3Enabled)) ? 'Beta' : 'Free';
      result.actions.push(
        actions.billing.getShop.fulfilled({
          channel,
          db,
          plan: PLAN.FREE,
          shopBadge,
          shopId,
          shopName,
        })
      );

      result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
      return result;
    }

    switch (channel) {
      case EBAY: {
        if (isEbayInvalidMarketplace(shop.get('syncStatus'))) {
          return result;
        }

        break;
      }

      default: {
        break;
      }
    }

    switch (plan) {
      case PLAN.TRIAL: {
        const discount = subscription.get('discountPercent');
        const trialRemaining = subscription.get('trialRemaining');
        const disabled = trialRemaining < 1;

        result.actions.push(
          actions.billing.getShop({
            channel,
            db,
            disabled,
            discount,
            dueDate: null,
            plan,
            shopId,
            shopName,
            signal,
            subscribed: false,
            trialRemaining,
          })
        );

        result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
        break;
      }

      case 'lite':
      case 'plus':
      case 'Vela Lite':
      case 'Vela Plus': {
        const dueDate = subscription.get('dueDate');
        const price = subscription.get('price');

        result.actions.push(
          actions.billing.getShop({
            channel,
            db,
            dueDate,
            shopId,
            shopName,
            signal,
            subscribed: true,
          })
        );

        result.shops = result.shops
          .set('ids', result.shops.get('ids').push(shopId))
          .set('amount', result.shops.get('amount') + price);

        break;
      }

      case TIER.T_0:
      case TIER.T_251:
      case TIER.T_501:
      case TIER.T_1001:
      case TIER.T_2501:
      case TIER.T_5001:
      case TIER.T_7501:
      case TIER.T_10001:
      case TIER.T_20001:
      case TIER.T_30001:
      case TIER.T_40001:
      case TIER.T_50001:
      case TIER.T_60001:
      case TIER.T_70001: {
        const cancelAtPeriodEnd = subscription.get('cancelAtPeriodEnd');
        const tierChangeDetected = subscription.get('tierChangeDetected');
        const subscribed = !cancelAtPeriodEnd && !tierChangeDetected;
        const dueDate = subscribed ? subscription.get('dueDate') : undefined;

        result.actions.push(
          actions.billing.getShop({
            canceled: !!cancelAtPeriodEnd,
            channel,
            db,
            dueDate,
            plan,
            shopId,
            shopName,
            signal,
            subscribed,
          })
        );

        if (subscribed) {
          const discount = subscription.get('discountPercent');
          const price = subscription.get('price');

          result.shops = result.shops.set('amount',
            result.shops.get('amount') + getDiscountedPrice({ discount, price })
          );
        }

        result.shops = result.shops.set('ids', result.shops.get('ids').push(shopId));
        break;
      }

      default: {
        Sentry.captureException('Unexpected shop plan', {
          extra: {
            db,
            shopId,
            subscription: subscription.toJS(),
          },
        });

        break;
      }
    }

    return result;
  }

  const { actions: actionsToDispatch, shops } = userShops.get('options').reduce(reduceShopIds, { actions: [], shops: empty });

  if (getSize(actionsToDispatch)) {
    yield function effect(dispatch) {
      actionsToDispatch.forEach(dispatch);
    };
  }

  return reduction.setIn(['billing', 'shops'], shops);
}

function* getShop(reduction, { signal, ...shop }) {
  yield function effect(dispatch) {
    api.billing
      .getSubscription({ db: shop.db, shopId: shop.shopId, signal })
      .then(
        (response) => {
          const {
            discount_percent: discount,
            price_without_discount_in_units: price,
            product_count: listings,
            tier_name: plan,
          } = response;

          dispatch(actions.billing.getShop.fulfilled({ discount, plan, price, listings, ...shop }));
        },
        (error) => dispatch(actions.billing.getShop.rejected({ error, shop, signal }))
      );
  };

  return reduction;
}

function* getShopFulfilled(reduction, shop) {
  let state = reduction.getIn(['billing', 'shops']);

  function addCount(count = 0) {
    return count + 1;
  }

  state = state
    .update('connected', addCount)
    .setIn(['byId', shop.shopId], Map(shop));

  if (shop.subscribed) {
    state = state.update('upgraded', addCount);
  }

  return reduction.setIn(['billing', 'shops'], state);
}

function* getShopRejected(reduction, { error, shop, signal }) {
  if (signal.aborted) return reduction;

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.setIn(['billing', 'shops', 'byId', shop.shopId], Map(shop));
}

function* manageShop(reduction, { shopId, type, ...rest } = {}) {
  abort(manageController);

  switch (type) {
    case MODALS.BILLING.MANAGE: {
      if (!reduction.hasIn(['shops', 'byId', shopId])) return reduction;

      const { signal } = manageController.start();
      const shop = reduction.getIn(['shops', 'byId', shopId]);
      const channel = shop.get('channel');
      const db = shop.get('db');

      if (shop.get('syncStatus') === STATUSES.TOKEN_REJECTED) {
        if (isSubscribed(reduction.getIn(['user', 'subscriptions', shopId]))) {
          rest.reconnect = ENDPOINT.CONNECT_CHANNEL[channel];
        } else {
          navigateTo(`/${shopId}`);
          return reduction;
        }
      }

      yield function effect(dispatch) {
        api.billing
          .getSubscription({ db, shopId, signal })
          .then(
            (response) => dispatch(actions.billing.manageShop.fulfilled({ response, shopId, signal })),
            (error) => dispatch(actions.billing.manageShop.rejected({ error, signal }))
          );
      };

      return reduction.setIn(['billing', 'manage'], Map({ channel, shopId, type, ...rest }));
    }

    case MODALS.BILLING.SUCCESS: {
      const shop = reduction.getIn(['shops', 'byId', shopId]);
      const channel = shop.get('channel');
      const shopName = shop.get('name');

      return reduction.setIn(['billing', 'manage'], Map({ channel, shopId, shopName, type, ...rest }));
    }

    default: {
      return reduction.deleteIn(['billing', 'manage']);
    }
  }
}

function* manageShopFulfilled(reduction, { response, shopId }) {
  abort(manageController);

  const {
    discount_percent: discount,
    product_count: listings,
    price_without_discount_in_units: price,
    tier_name: plan,
    sync_status: syncStatus,
  } = response;

  const shop = reduction.getIn(['shops', 'byId', shopId]);

  if (syncStatus === STATUSES.TOKEN_REJECTED && shop.get('syncStatus') !== syncStatus) {
    yield function effect(dispatch) {
      dispatch(actions.shops.getShops());
      navigateTo(`/${shopId}`);
    };

    return reduction.setIn(['shops', 'byId', shopId, 'syncStatus'], syncStatus);
  }

  function updateData(data) {
    const channel = shop.get('channel');
    const db = shop.get('db');
    const name = shop.get('name');

    return data.set('shop',
      Map({
        channel,
        db,
        discount: parseInt(discount) || 0,
        id: shopId,
        listings: listings || 0,
        name,
        price: price || 0,
        plan,
        syncStatus,
      })
    );
  }

  return reduction.updateIn(['billing', 'manage'], updateData);
}

function* manageShopRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(manageController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['billing', 'manage']);
}

function* setInvoicesTab(reduction, index) {
  abort(invoicesController);

  const tab = reduction.getIn(['billing', 'invoices', 'tabs', index]);

  switch (tab.get('channel')) {
    case EBAY:
    case ETSY: {
      const shopId = tab.get('shopId');

      if (reduction.hasIn(['billing', 'invoices', 'byShopId', shopId])) break;

      const { signal } = invoicesController.start();
      const db = tab.get('db');
      const subscriptionId = tab.get('subscriptionId');

      yield function effect(dispatch) {
        api.billing
          .getInvoices({ db, shopId, signal, subscriptionId })
          .then(
            (response) => dispatch(actions.billing.setInvoicesTab.fulfilled({ index, response, shopId })),
            (error) => dispatch(actions.billing.setInvoicesTab.rejected({ error, signal }))
          );
      };

      break;
    }

    default: {
      break;
    }
  }

  return reduction.setIn(['billing', 'invoices', 'tab'], index);
}

function* setInvoicesTabFulfilled(reduction, { index, response, shopId }) {
  abort(invoicesController);

  if (getSize(response.invoices)) {
    return reduction.setIn(['billing', 'invoices', 'byShopId', shopId], fromJS(response.invoices));
  }

  const tabs = reduction.getIn(['billing', 'invoices', 'tabs']).delete(index);
  const tab = Math.min(index, getSize(tabs) - 1);

  if (getSize(tabs)) {
    yield function effect(dispatch) {
      dispatch(actions.billing.setInvoicesTab(tab));
    };
  }

  return reduction
    .setIn(['billing', 'invoices', 'tab'], tab)
    .setIn(['billing', 'invoices', 'tabs'], tabs);
}

function* setInvoicesTabRejected(reduction, { error, signal }) {
  if (signal.aborted) return reduction;

  abort(invoicesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_INVOICES,
      })
    );
  };

  return reduction;
}

function* togglePaymentMethodModal(reduction, paymentError ) {
  let billing = reduction.get('billing');
  if (paymentError) {
    billing = billing.setIn(['paymentError'], paymentError);
  }

  billing = billing.getIn(['showPaymentMethodModal'])
    ? billing.deleteIn(['showPaymentMethodModal'])
    : billing.setIn(['showPaymentMethodModal'], true);

  return reduction.set('billing', billing);
}

const billing = {
  [actions.billing.getCustomer]: getCustomer,
  [actions.billing.getCustomer.fulfilled]: getCustomerFulfilled,
  [actions.billing.getCustomer.rejected]: getCustomerRejected,
  [actions.billing.cleanUp]: cleanUp,
  [actions.billing.getInvoices]: getInvoices,
  [actions.billing.getShops]: getShops,
  [actions.billing.getShop]: getShop,
  [actions.billing.getShop.fulfilled]: getShopFulfilled,
  [actions.billing.getShop.rejected]: getShopRejected,
  [actions.billing.setInvoicesTab]: setInvoicesTab,
  [actions.billing.setInvoicesTab.fulfilled]: setInvoicesTabFulfilled,
  [actions.billing.setInvoicesTab.rejected]: setInvoicesTabRejected,
  [actions.billing.manageShop]: manageShop,
  [actions.billing.manageShop.fulfilled]: manageShopFulfilled,
  [actions.billing.manageShop.rejected]: manageShopRejected,
  [actions.billing.togglePaymentMethodModal]: togglePaymentMethodModal,
};

export default billing;
